import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { match } from 'ts-pattern';

import { useCreateInvoice, useCreditableInvoices } from '~/api/invoices';
import { useBillitSsoUrl, useUser } from '~/api/user';
import { Container, Header, Menu, Tabs } from '~/components';
import { productFruitsIds } from '~/constants/productFruits';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { toast } from '~/utils/toast';

export const InvoicesCreditNotesLayout = () => {
  const { data: user } = useUser();
  const { data: creditableInvoices } = useCreditableInvoices();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'invoices', 'creditnotes', 'invoiceTemplates']);

  const createDraftInvoiceMutation = useCreateInvoice();
  const getBillitSsoUrlMutation = useBillitSsoUrl();

  const createDraftInvoice = () => {
    createDraftInvoiceMutation.mutate(undefined, {
      onSuccess: ({ id }) => navigate(r(routes.createInvoice, { invoiceId: id })),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  const { pathname } = useLocation();

  const createBillitInvoice = () => {
    if (!user.integrations.billit) return navigate(routes.settingsIntegrations);

    getBillitSsoUrlMutation.mutate(undefined, {
      onSuccess: (redirectUrl) => window.open(redirectUrl, '_blank')?.focus(),
      onError: () => toast.danger({ description: t('common:error') }),
    });
  };

  const primaryAction = match(pathname.replace(/\/$/, ''))
    .with(routes.invoices, () => 'createInvoice' as const)
    .with(routes.creditNotes, () => 'createCreditNote' as const)
    .with(routes.invoiceTemplates, () => 'createInvoiceTemplate' as const)
    .otherwise(() => 'createInvoice' as const);

  return (
    <>
      <Header.Root>
        <Header.Content title={t('invoices:header.title')} />

        <Header.Actions>
          <Header.ActionGroup floatingIcon="Add" loading={createDraftInvoiceMutation.isPending}>
            {match(primaryAction)
              .with(
                'createCreditNote',
                () => creditableInvoices.length > 0,
                () => (
                  <Header.LinkAction
                    data-pf-id={productFruitsIds.creditNotes.header.createCreditNote}
                    icon="Add"
                    to={routes.createCreditNote}
                  >
                    {t('invoices:header.actionBlock.dropdown.creditnote')}
                  </Header.LinkAction>
                ),
              )
              .with('createInvoiceTemplate', () => (
                <Header.LinkAction
                  data-pf-id={productFruitsIds.invoiceTemplates.header.createInvoiceTemplate}
                  icon="Add"
                  to={routes.createInvoiceTemplate}
                >
                  {t('invoices:header.actionBlock.dropdown.invoiceTemplate')}
                </Header.LinkAction>
              ))
              .otherwise(() => (
                <Header.Action
                  data-pf-id={productFruitsIds.invoices.header.createInvoice}
                  icon="Add"
                  loading={createDraftInvoiceMutation.isPending}
                  onClick={createDraftInvoice}
                >
                  {t('invoices:header.actionBlock.button')}
                </Header.Action>
              ))}

            <Header.ActionGroupMenu loading={primaryAction !== 'createInvoice' && createDraftInvoiceMutation.isPending}>
              {primaryAction !== 'createInvoice' && (
                <Menu.Item disabled={createDraftInvoiceMutation.isPending} icon="Add" onClick={createDraftInvoice}>
                  {t('invoices:header.actionBlock.button')}
                </Menu.Item>
              )}

              {primaryAction !== 'createCreditNote' && creditableInvoices.length > 0 && (
                <Menu.LinkItem icon="Add" to={routes.createCreditNote}>
                  {t('invoices:header.actionBlock.dropdown.creditnote')}
                </Menu.LinkItem>
              )}

              {primaryAction !== 'createInvoiceTemplate' && (
                <Menu.LinkItem icon="Add" to={routes.createInvoiceTemplate}>
                  {t('invoices:header.actionBlock.dropdown.invoiceTemplate')}
                </Menu.LinkItem>
              )}

              {user.integrations.billit ? (
                <Menu.Item icon="Add" onClick={createBillitInvoice}>
                  {t('invoices:header.actionBlock.dropdown.notCreative')}
                </Menu.Item>
              ) : (
                <Menu.LinkItem icon="Add" to={routes.settingsIntegrations}>
                  {t('invoices:header.actionBlock.dropdown.notCreative')}
                </Menu.LinkItem>
              )}
            </Header.ActionGroupMenu>
          </Header.ActionGroup>
        </Header.Actions>

        <Header.Tabs>
          <Tabs.Root value={pathname.replace(/\/$/, '')}>
            <Tabs.List>
              <Tabs.TabLink icon="RequestQuote" preventScrollReset replace to={routes.invoices} value={routes.invoices}>
                {t('invoices:overview.created.subtitle')}
              </Tabs.TabLink>

              <Tabs.TabLink icon="CreditNote" preventScrollReset replace to={routes.creditNotes} value={routes.creditNotes}>
                {t('creditnotes:overview.created.subtitle')}
              </Tabs.TabLink>

              <Tabs.TabLink icon="InvoiceTemplate" preventScrollReset replace to={routes.invoiceTemplates} value={routes.invoiceTemplates}>
                {t('invoiceTemplates:overview.header.title')}
              </Tabs.TabLink>
            </Tabs.List>
          </Tabs.Root>
        </Header.Tabs>
      </Header.Root>

      <Container>
        <Outlet />
      </Container>
    </>
  );
};
